import React, { useContext, useState } from 'react';
import DialogBox from '../../../Common/Layout/DialogBox';
import {
  IDividendGross,
  useCreateDividendMutation,
  useUpdateDividendMutation
} from '../../../../generated/graphql';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { parseDate } from '../../../../utils/clientUtils';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { Colors } from '../../../../utils/theme';
import InputComponent from '../../../Common/Fields/InputComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, dividendInput?: IDividendGross) => void;
  dividendInput: IDividendGross | null;
}

type IErrors = {
  value: string | null;
  date: string | null;
};

const defaultErrorsObj: IErrors = {
  value: null,
  date: null
};

const errorTexts = {
  date: 'Date is required!',
  value: 'Dividend Gross is required!'
};

const DividendGrossForm: React.FunctionComponent<IProps> = ({
  dividendInput,
  stockId,
  handleClose
}: IProps) => {
  const [editable, setEditable] = useState(dividendInput === null);
  const [dividendValue, setDividendValue] = useState<number | null>(dividendInput?.value || null);
  const [date, setDate] = useState<Date | null>(
    dividendInput ? parseDate(dividendInput.start_date) : new Date()
  );
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createDividend, createDividendResult] = useCreateDividendMutation();
  const [updateDividend, updateDividendResult] = useUpdateDividendMutation();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Stock);

  const handleCancel = () => {
    if (editable && dividendInput !== null) {
      setDividendValue(dividendInput?.value || null);
      setDate(parseDate(dividendInput.start_date));
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.currentTarget.value === '' ? null : Number(event.currentTarget.value);
    if (value === null) {
      setFormErrors({ ...formErrors, value: errorTexts.value });
    } else {
      setFormErrors({ ...formErrors, value: null });
    }
    setDividendValue(value);
  };

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, date: null });
      }
    } else {
      setFormErrors({ ...formErrors, date: errorTexts.date });
    }
    setDate(input);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      date: null,
      value: null
    };
    if (!date) {
      hasErrors = true;
      errors.date = errorTexts.date;
    }
    if (!dividendValue) {
      hasErrors = true;
      errors.value = errorTexts.value;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(dividendInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createDividend({
        variables: {
          stock_id: stockId,
          input: {
            start_date: (date as Date).toString(),
            value: dividendValue as number
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IDividendGross);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Dividend created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateDividend({
        variables: {
          id: dividendInput?.id as string,
          input: {
            value: Number(dividendValue),
            start_date: (date as Date).toString()
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IDividendGross);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Dividend updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="dividend-dialog"
      width={410}
      title={dividendInput ? 'Dividend Details' : 'Create Dividend'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {dividendInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="divident-gross-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box display="flex" gap="15px" sx={{ my: '20px' }}>
        <InputComponent
          id="divident-value"
          label="Divident Gross"
          type="number"
          value={dividendValue}
          onChange={handleValueChange}
          disabled={createDividendResult.loading || updateDividendResult.loading || !editable}
          errorMsg={formErrors.value}
        />
        <Box sx={{ width: '100%' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="dividend-date-picker"
              label="Date"
              value={date}
              disabled={createDividendResult.loading || updateDividendResult.loading || !editable}
              onChange={(d) => handleDateChange(d)}
              errorMsg={formErrors.date}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-dividend-gross-button"
          onClick={handleCancel}
          disabled={createDividendResult.loading || updateDividendResult.loading}
          text={dividendInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-dividend-gross-button"
          data-testid="save-dividend-gross-button"
          onClick={handleSubmit}
          disabled={createDividendResult.loading || updateDividendResult.loading || !editable}
          text={dividendInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default DividendGrossForm;
